import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Input, message, Tooltip } from 'antd';
import { Edit, MoreVertical, Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import type { KanbanTask, KanbanTaskStatus } from '../../stores/KanbanTaskStores';
import type { TaskStore } from '../../stores/kanban/TaskStore';
import type { Employee } from '../../models/Employee';
import InlineTaskForm from './InlineTaskForm';
import TaskCard from './TaskCard';
import type { BoardDnd } from './useBoardDnd';
import type { QuickAdd } from './useQuickAdd';

export type ColumnProps = {
  status: KanbanTaskStatus;
  /** Position in the rendered order; the column drop indicator is keyed on it. */
  index: number;
  /** Already filtered and sorted by the page. */
  tasks: KanbanTask[];
  store: TaskStore;
  isReadOnly: boolean;
  /** Anonymous (public share-link) board — see the comment on the "delete" menu item below. */
  isAnonymous: boolean;
  dnd: BoardDnd;
  quickAdd: QuickAdd;
  onOpenTask: (task: KanbanTask) => void;
  /** Delete stays on the page: Task 12 hangs the "move tasks first" modal off that one call site. */
  onDeleteStatus: (statusId: string) => void;
  t: (key: string) => string;
  assignableUsers?: Employee[];
  baseCloudinary?: string;
};

const Column: React.FC<ColumnProps> = observer(
  ({
    status,
    index,
    tasks,
    store,
    isReadOnly,
    isAnonymous,
    dnd,
    quickAdd,
    onOpenTask,
    onDeleteStatus,
    t,
    assignableUsers,
    baseCloudinary,
  }) => {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(status.name);
    const [isFinalStatus, setIsFinalStatus] = useState(status.isFinalStatus);

    const startEditing = () => {
      setName(status.name);
      setIsFinalStatus(status.isFinalStatus);
      setEditing(true);
    };

    const save = async () => {
      if (!name.trim()) return;
      setEditing(false);
      // The store patches `projectStatuses` itself and reports a failure by returning null.
      const result = await store.updateStatus(status.id, name.trim(), isFinalStatus);
      if (result) {
        message.success(t('ColumnUpdated'));
      } else {
        message.error(t('FailedToUpdateColumn'));
      }
    };

    return (
      <div className="column-wrapper">
        {dnd.columnDropTarget?.index === index && dnd.draggedColumn && <div className="column-drop-indicator" />}
        <div
          className={`column ${dnd.dropTarget?.statusId === status.id ? 'column-drop-target' : ''}`}
          onDragOver={(e) => dnd.onHeaderDragOver(e, index)}
          onDrop={(e) => {
            void dnd.onHeaderDrop(e, index);
          }}
        >
          <div
            className="column-header hover-effect"
            draggable={!isReadOnly}
            onDragStart={(e) => dnd.onHeaderDragStart(e, status.id)}
            onDragEnd={dnd.onHeaderDragEnd}
            style={{ minHeight: editing ? '130px' : 'auto' }}
          >
            {editing ? (
              <div className="column-edit-form">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  className="column-edit-input hover-effect"
                />
                <Checkbox
                  checked={isFinalStatus}
                  onChange={(e) => setIsFinalStatus(e.target.checked)}
                  style={{ marginTop: 8 }}
                >
                  {t('IsFinalStatus')}
                  <Tooltip title={t('FinalStatusTooltip')}>
                    <InfoCircleOutlined style={{ marginLeft: 4 }} />
                  </Tooltip>
                </Checkbox>
                <div className="column-edit-actions">
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      void save();
                    }}
                    className="column-save-button hover-effect"
                  >
                    {t('Save')}
                  </Button>
                  <Button size="small" onClick={() => setEditing(false)} className="column-cancel-button hover-effect">
                    {t('Cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="column-title kanban-headers">
                  {status.name} ({tasks.length})
                </div>
                <div className="column-actions">
                  <Dropdown
                    menu={{
                      // `isAnonymous` hides "delete" here on purpose: this is a deliberate product
                      // decision (2026-09, requested by the user), not an oversight and not decision 4
                      // (decision 4 only says a share-link visitor is not read-only — it never said every
                      // write decision 4 unlocked has to stay unlocked forever). Deletion is refused
                      // server-side too (Features:PublicBoardAllowDelete, default off) — this gate just
                      // keeps the menu from offering a button that would fail. Do not remove this to
                      // "restore parity" without checking with the user first.
                      items: [
                        { key: 'edit', label: t('Edit'), icon: <Edit size={14} /> },
                        ...(isAnonymous
                          ? []
                          : [{ key: 'delete', label: t('Delete'), icon: <Trash2 size={14} />, danger: true }]),
                      ],
                      onClick: ({ key }) => (key === 'edit' ? startEditing() : onDeleteStatus(status.id)),
                    }}
                    trigger={['click']}
                    disabled={isReadOnly}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreVertical size={16} />}
                      onClick={(e) => e.preventDefault()}
                      className="column-menu-button hover-effect"
                    />
                  </Dropdown>
                </div>
              </>
            )}
          </div>

          <div
            ref={(el) => dnd.registerColumn(status.id, el)}
            className="column-content"
            onDragOver={(e) => dnd.onColumnDragOver(e, status.id, tasks)}
            onDragEnter={(e) => dnd.onColumnDragEnter(e, status.id, tasks)}
            onDrop={(e) => {
              void dnd.onColumnDrop(e, status.id);
            }}
          >
            {tasks.map((task, taskIndex) => (
              <React.Fragment key={task.id}>
                {dnd.dropTarget?.statusId === status.id && dnd.dropTarget.index === taskIndex && (
                  <div className="task-drop-indicator" />
                )}
                <TaskCard
                  task={task}
                  onTaskClick={onOpenTask}
                  handleDragStart={dnd.onCardDragStart}
                  handleDragEnd={dnd.onCardDragEnd}
                  cardRef={(el) => dnd.registerCard(String(task.id), el)}
                  t={t}
                />
              </React.Fragment>
            ))}
            {dnd.dropTarget?.statusId === status.id && dnd.dropTarget.index === tasks.length && (
              <div className="task-drop-indicator" />
            )}

            {quickAdd.openInStatus === status.id ? (
              <InlineTaskForm
                statusId={status.id}
                onCancel={quickAdd.cancel}
                onSubmit={(statusId, title, description, dueDate, assigneeId) => {
                  void quickAdd.submit(statusId, title, description, dueDate, assigneeId);
                }}
                assignableUsers={assignableUsers}
                baseCloudinary={baseCloudinary}
              />
            ) : (
              <Button
                type="dashed"
                icon={<Plus size={16} />}
                block
                onClick={() => quickAdd.open(status.id)}
                className="add-column-task-button hover-effect"
                disabled={isReadOnly}
              >
                {t('AddTask')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default Column;
